import React from 'react';
import {
	Badge,
	Box,
	Button,
	Divider,
	Flex,
	FormControl,
	FormErrorMessage,
	FormLabel,
	HStack,
	Icon,
	Input,
	InputGroup,
	InputLeftElement,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Select,
	Spinner,
	Text,
	VStack,
	useBreakpointValue,
} from '@chakra-ui/react';
import { FiArrowLeft, FiRefreshCw, FiSearch, FiSend } from 'react-icons/fi';

import useAppToast from 'components/toast';
import { useListConversationTemplatesQuery, useSendMessageMutation, useSyncTemplatesMutation } from 'api/metaWhatsappApi';
import { usePermissions } from 'hooks/usePermissions';

import TemplateParts from './TemplateParts.jsx';
import { explainRefusal } from './sendErrors';
import { describeKey, headerIsUnsupported, isComplete, problemWith, requiredKeys } from './templateVariables';

/**
 * Choosing an approved template, filling it in, and sending it.
 *
 * ★ A MODAL, NOT A MENU OR A POPOVER.
 * This opens from the composer, which also lives inside the lead drawer. A
 * Chakra Menu or Popover portals to the document body and lands BEHIND the
 * drawer's overlay — see `portalTarget.jsx`. A Modal does not, so the same
 * component works in the inbox and in the drawer with no special case.
 *
 * ★ THE LIST IS THE SERVER'S ANSWER FOR THIS CONVERSATION.
 * `/conversations/:id/templates` returns only what may actually be sent here,
 * authorised by the same question the send will ask. The browser never names a
 * channel and never learns which Meta account is behind the number.
 *
 * ★ THE BROWSER SENDS VALUES, NEVER COMPONENTS.
 * The payload is `{name, language, values}` and the server rebuilds the
 * components from its own mirror. A body that supplied its own components would
 * be an arbitrary image or link sent from the business's verified number, and
 * the server refuses one outright.
 *
 * ★ WHAT WAS TYPED SURVIVES A FAILED SEND.
 * The values are cleared when the agent changes template, changes conversation
 * or sends successfully — never because the server said no. Losing a filled
 * form to a transient refusal is how an agent ends up retyping a customer's
 * name three times.
 */

const useDebounced = (value, ms = 180) => {
	const [settled, setSettled] = React.useState(value);
	React.useEffect(() => {
		const t = setTimeout(() => setSettled(value), ms);
		return () => clearTimeout(t);
	}, [value, ms]);
	return settled;
};

/** "follow_up_ar" → "Follow up ar". */
const humanise = (name) =>
	String(name ?? '')
		.replace(/[_-]+/g, ' ')
		.trim()
		.replace(/^./, (c) => c.toUpperCase());

/** The body text of a template, for the searchable summary in the list. */
const summarise = (template) => {
	const body = (template?.components ?? []).find((c) => String(c?.type).toUpperCase() === 'BODY');
	return String(body?.text ?? '').replace(/\s+/g, ' ').trim();
};

function TemplateRow({ template, isActive, onPick }) {
	const count = requiredKeys(template).length;
	const unsupported = headerIsUnsupported(template);

	return (
		<Box
			as="button"
			type="button"
			textAlign="left"
			w="100%"
			px="3"
			py="2"
			borderRadius="md"
			bg={isActive ? 'wa.selected' : 'transparent'}
			_hover={{ bg: isActive ? 'wa.selected' : 'wa.hover' }}
			onClick={() => onPick(template)}
			opacity={unsupported ? 0.55 : 1}
		>
			<HStack spacing="2" align="baseline">
				<Text fontSize="sm" fontWeight="semibold" color="text.primary" noOfLines={1} flex="1" minW="0">
					{humanise(template.name)}
				</Text>
				<Badge fontSize="0.6rem" textTransform="uppercase" colorScheme="gray" flexShrink={0}>
					{template.language}
				</Badge>
			</HStack>
			<Text fontSize="xs" color="text.muted" noOfLines={2} mt="0.5">
				{summarise(template) || 'No message body'}
			</Text>
			<HStack spacing="2" mt="1">
				{template.category && (
					<Text fontSize="0.65rem" color="text.muted" textTransform="capitalize">
						{String(template.category).toLowerCase()}
					</Text>
				)}
				{count > 0 && (
					<Text fontSize="0.65rem" color="text.muted">
						{count} {count === 1 ? 'value' : 'values'}
					</Text>
				)}
				{unsupported && (
					<Text fontSize="0.65rem" color="warning">
						Needs an attachment
					</Text>
				)}
			</HStack>
		</Box>
	);
}

export default function TemplatePicker({ isOpen, onClose, conversationId }) {
	const notify = useAppToast();
	const { hasPermission } = usePermissions();
	const isMobile = useBreakpointValue({ base: true, md: false }, 'md') ?? false;

	const [search, setSearch] = React.useState('');
	const [language, setLanguage] = React.useState('');
	const [category, setCategory] = React.useState('');
	const [picked, setPicked] = React.useState(null);
	const [values, setValues] = React.useState({});
	const [touched, setTouched] = React.useState({});

	const debouncedSearch = useDebounced(search);

	// ★ `currentData`, NEVER `data`. RTK Query keeps the last successful result
	// when the arguments change or the query is skipped, so `data` would show
	// the previous conversation's catalogue while this one loads — and on a
	// picker that decides what a customer receives, that is not a nicety.
	const { currentData: templates, isFetching, isError, error, refetch } = useListConversationTemplatesQuery(
		{ conversationId },
		{ skip: !isOpen || !conversationId },
	);

	const [sendMessage, { isLoading: isSending }] = useSendMessageMutation();
	const [syncTemplates, { isLoading: isSyncing }] = useSyncTemplatesMutation();
	const canSync = hasPermission('whatsapp', 'template.sync');

	const list = React.useMemo(() => templates ?? [], [templates]);

	// Filters are applied in the browser: one account's approved catalogue is
	// tens of rows, and a round trip per keystroke would be slower and no more
	// correct. Language and category still exist as server parameters for
	// callers that want them.
	const filtered = React.useMemo(() => {
		const needle = debouncedSearch.trim().toLowerCase();
		return list.filter((t) => {
			if (language && t.language !== language) return false;
			if (category && String(t.category ?? '').toUpperCase() !== category) return false;
			if (!needle) return true;
			return `${t.name} ${summarise(t)}`.toLowerCase().includes(needle);
		});
	}, [list, debouncedSearch, language, category]);

	const languages = React.useMemo(() => [...new Set(list.map((t) => t.language).filter(Boolean))].sort(), [list]);
	const categories = React.useMemo(
		() => [...new Set(list.map((t) => String(t.category ?? '').toUpperCase()).filter(Boolean))].sort(),
		[list],
	);

	const keys = React.useMemo(() => requiredKeys(picked), [picked]);
	const complete = picked && isComplete(picked, values);
	const unsupported = picked && headerIsUnsupported(picked);

	// ★ RESET ON CONVERSATION CHANGE, NOT ON CLOSE.
	// Reopening the same thread keeps what was being typed; moving to a
	// different customer must not, because a half-filled form carrying the
	// previous customer's name is how the wrong name reaches the right person.
	React.useEffect(() => {
		setPicked(null);
		setValues({});
		setTouched({});
		setSearch('');
		setLanguage('');
		setCategory('');
	}, [conversationId]);

	const choose = (template) => {
		setPicked(template);
		// A different template means different parameters; keeping the old
		// values would silently map one template's answers onto another's slots.
		setValues({});
		setTouched({});
	};

	const send = async () => {
		if (!picked || !complete) return;
		try {
			await sendMessage({
				conversationId,
				type: 'template',
				// Values only. The server builds the components from its mirror.
				template: { name: picked.name, language: picked.language, values },
				// The picker already holds the definition it is previewing, so the
				// pending bubble can render properly instead of appearing blank.
				templatePreview: {
					name: picked.name,
					language: picked.language,
					components: picked.components,
					values,
				},
				// The idempotency key, so a double-click cannot send twice.
				clientMessageId: `tpl_${conversationId}_${Date.now()}`,
			}).unwrap();
			notify.success('Template sent', `${humanise(picked.name)} is on its way.`);
			setPicked(null);
			setValues({});
			setTouched({});
			onClose();
		} catch (err) {
			// Deliberately keeps `picked` and `values`: the agent fixes what the
			// server named and presses Send again.
			notify.error('Not sent', explainRefusal(err?.data));
		}
	};

	// Mobile is the same modal as two steps — list, then fill — mirroring the
	// inbox's own two-pane slide rather than cramming both into one column.
	const showList = !isMobile || !picked;
	const showDetail = !isMobile || Boolean(picked);

	const refresh = async () => {
		try {
			if (canSync) await syncTemplates().unwrap();
			else await refetch();
		} catch (err) {
			notify.error('Could not refresh', explainRefusal(err?.data));
		}
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} isCentered={!isMobile} size={isMobile ? 'full' : '4xl'} scrollBehavior="inside">
			<ModalOverlay />
			<ModalContent bg="bg.surface" maxH={isMobile ? '100%' : '38rem'}>
				<ModalHeader color="text.primary" fontSize="md" pb="2">
					<HStack spacing="2">
						{isMobile && picked && (
							<Box
								as="button"
								type="button"
								onClick={() => setPicked(null)}
								aria-label="Back to the list"
								px="1"
							>
								<Icon as={FiArrowLeft} />
							</Box>
						)}
						<Text>{isMobile && picked ? humanise(picked.name) : 'Send a template'}</Text>
					</HStack>
				</ModalHeader>
				<ModalCloseButton />

				<ModalBody p="0" overflow="hidden">
					<Flex h={isMobile ? 'auto' : '26rem'} direction={{ base: 'column', md: 'row' }}>
						{showList && (
							<Box
								w={{ base: '100%', md: '22rem' }}
								borderRightWidth={{ base: 0, md: '1px' }}
								borderColor="border.subtle"
								display="flex"
								flexDirection="column"
								minH="0"
							>
								<VStack spacing="2" px="4" pb="2" align="stretch" flexShrink={0}>
									<InputGroup size="sm">
										<InputLeftElement pointerEvents="none">
											<Icon as={FiSearch} color="text.muted" boxSize="14px" />
										</InputLeftElement>
										<Input
											placeholder="Search templates"
											value={search}
											onChange={(e) => setSearch(e.target.value)}
											borderRadius="md"
										/>
									</InputGroup>
									{(languages.length > 1 || categories.length > 1) && (
										<HStack spacing="2">
											{languages.length > 1 && (
												<Select size="sm" value={language} onChange={(e) => setLanguage(e.target.value)} borderRadius="md">
													<option value="">All languages</option>
													{languages.map((l) => (
														<option key={l} value={l}>
															{l}
														</option>
													))}
												</Select>
											)}
											{categories.length > 1 && (
												<Select size="sm" value={category} onChange={(e) => setCategory(e.target.value)} borderRadius="md">
													<option value="">All categories</option>
													{categories.map((c) => (
														<option key={c} value={c}>
															{c.toLowerCase()}
														</option>
													))}
												</Select>
											)}
										</HStack>
									)}
								</VStack>

								<Box flex="1" overflowY="auto" px="2" pb="2" minH={isMobile ? '14rem' : 0}>
									{isFetching && !list.length && (
										<HStack px="2" py="3" spacing="2">
											<Spinner size="sm" />
											<Text fontSize="sm" color="text.muted">
												Loading templates…
											</Text>
										</HStack>
									)}

									{isError && !isFetching && (
										<Text fontSize="sm" color="text.muted" px="2" py="3">
											{explainRefusal(error?.data)}
										</Text>
									)}

									{!isFetching && !isError && list.length === 0 && (
										<Text fontSize="sm" color="text.muted" px="2" py="3">
											No approved templates on this number yet. An administrator can add them in WhatsApp Manager.
										</Text>
									)}

									{!isError && list.length > 0 && filtered.length === 0 && (
										<Text fontSize="sm" color="text.muted" px="2" py="3">
											Nothing matches that search.
										</Text>
									)}

									<VStack spacing="0.5" align="stretch">
										{filtered.map((t) => (
											<TemplateRow
												key={`${t.name}|${t.language}`}
												template={t}
												isActive={picked?.name === t.name && picked?.language === t.language}
												onPick={choose}
											/>
										))}
									</VStack>
								</Box>
							</Box>
						)}

						{showDetail && (
							<Box flex="1" minW="0" overflowY="auto" px="4" pb="4" pt={{ base: 2, md: 0 }}>
								{!picked && (
									<Flex h="100%" align="center" justify="center" px="6">
										<Text fontSize="sm" color="text.muted" textAlign="center">
											Pick a template to see what the customer will receive.
										</Text>
									</Flex>
								)}

								{picked && (
									<VStack align="stretch" spacing="3">
										{/* The preview is a WhatsApp bubble, on the chat's own
										    palette, rendered by the SAME component the thread
										    uses — so what is approved here is what appears
										    there. */}
										<Box>
											<Text fontSize="xs" color="text.muted" mb="1.5">
												Preview
											</Text>
											<Box bg="wa.outgoing" color="wa.ink" borderRadius="lg" px="2.5" py="2" maxW="26rem">
												<TemplateParts components={picked.components} values={values} />
											</Box>
										</Box>

										{unsupported && (
											<Text fontSize="sm" color="warning">
												This template needs an image or file in its header, which cannot be sent from the inbox
												yet. Use it in a campaign instead.
											</Text>
										)}

										{keys.length === 0 && !unsupported && (
											<Text fontSize="sm" color="text.muted">
												This template has no values to fill in — it is ready to send.
											</Text>
										)}

										{keys.length > 0 && (
											<>
												<Divider borderColor="border.subtle" />
												<VStack align="stretch" spacing="2.5">
													{keys.map((key) => {
														const problem = problemWith(values[key]);
														const show = Boolean(touched[key] && problem);
														return (
															<FormControl key={key} isInvalid={show}>
																<FormLabel fontSize="xs" color="text.muted" mb="1">
																	{describeKey(key)}
																</FormLabel>
																<Input
																	size="sm"
																	borderRadius="md"
																	value={values[key] ?? ''}
																	placeholder={`Value for ${describeKey(key).toLowerCase()}`}
																	onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
																	onBlur={() => setTouched((t) => ({ ...t, [key]: true }))}
																/>
																{show && <FormErrorMessage fontSize="xs">{problem}</FormErrorMessage>}
															</FormControl>
														);
													})}
												</VStack>
											</>
										)}
									</VStack>
								)}
							</Box>
						)}
					</Flex>
				</ModalBody>

				<ModalFooter borderTopWidth="1px" borderColor="border.subtle" py="3">
					<HStack spacing="2" w="100%" justify="space-between">
						{/* A newly approved template is invisible until the mirror is
						    re-read, and nothing re-reads it on a schedule. This is the
						    agent's way out of "it is approved but not in the list". */}
						<Button
							size="sm"
							variant="ghost"
							leftIcon={<Icon as={FiRefreshCw} />}
							onClick={refresh}
							isLoading={isSyncing || (isFetching && Boolean(list.length))}
							flexShrink={0}
						>
							Refresh
						</Button>
						<HStack spacing="2" flexShrink={0}>
							<Button size="sm" variant="ghost" onClick={onClose}>
								Cancel
							</Button>
							<Button
								size="sm"
								colorScheme="green"
								leftIcon={<Icon as={FiSend} />}
								onClick={send}
								isLoading={isSending}
								isDisabled={!picked || !complete || unsupported}
							>
								Send template
							</Button>
						</HStack>
					</HStack>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
}
